import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../lib/LangContext";
import { SITE } from "../lib/siteContent";
import { IMAGES } from "../lib/content";
import { Wordmark, MonogramSeal } from "../components/Logo";
import SiteNav from "../components/SiteNav";
import SmoothScroll from "../components/SmoothScroll";
import Marquee from "../components/Marquee";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/* ---------- Reveal helper ---------- */
function Reveal({ children, delay = 0, y = 30, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, delay, ease: [0.7, 0, 0.2, 1] }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/* ---------- Hero ---------- */
function Hero() {
    const { lang } = useLang();
    const t = SITE.hero[lang];
    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 0.25], [0, -80]);
    const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.4]);

    const goto = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="hero"
            data-testid="site-hero"
            className="relative min-h-[100vh] paper flex flex-col justify-center overflow-hidden"
        >
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 1.2 }}
                className="absolute top-24 md:top-28 left-6 md:left-12 right-6 md:right-12 flex justify-between font-micro text-[10px] md:text-[11px]"
                style={{ color: "#0B0B0B", letterSpacing: "0.4em" }}
            >
                <span>{t.kicker}</span>
                <span className="hidden md:inline">® REGISTERED TRADEMARK</span>
                <span className="hidden md:inline">TRAPANI · SICILIA</span>
            </motion.div>

            <motion.div style={{ y, opacity }} className="column-12 pt-32 md:pt-24 pb-16">
                <div className="col-span-12 md:col-span-11">
                    {t.title.map((line, i) => (
                        <div key={i} className="overflow-hidden">
                            <motion.h1
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ delay: 0.5 + i * 0.15, duration: 1.1, ease: [0.7, 0, 0.2, 1] }}
                                className="font-didone font-normal leading-[0.95] tracking-tight"
                                style={{
                                    fontSize: "clamp(2.8rem, 9vw, 9rem)",
                                    color: "#0B0B0B",
                                }}
                            >
                                {i === 1 ? (
                                    <em className="italic font-serif-lux" style={{ fontWeight: 300 }}>
                                        {line}
                                    </em>
                                ) : (
                                    line
                                )}
                            </motion.h1>
                        </div>
                    ))}
                </div>

                <div className="col-span-12 md:col-span-6 md:col-start-6 mt-12">
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 1.4, duration: 1.2, ease: [0.7, 0, 0.2, 1] }}
                        className="h-px w-32 mb-8"
                        style={{ background: "#C9A96A", transformOrigin: "left" }}
                    />
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.6, duration: 1 }}
                        className="font-serif-lux text-xl md:text-2xl lg:text-3xl leading-[1.4] max-w-3xl"
                    >
                        {t.intro}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2.1, duration: 1 }}
                        className="mt-12 flex flex-wrap items-center gap-8"
                    >
                        <button
                            data-testid="hero-cta-origins"
                            onClick={() => goto("origins")}
                            className="group font-micro text-[10px] md:text-xs flex items-center gap-3"
                            style={{ letterSpacing: "0.4em" }}
                        >
                            <span className="link-ed">{t.cta1.toUpperCase()}</span>
                            <span className="inline-block transition-transform group-hover:translate-x-2">→</span>
                        </button>
                        <Link
                            to="#follow"
                            data-testid="hero-cta-book"
                            onClick={(e) => {
                                e.preventDefault();
                                goto("follow");
                            }}
                            className="group font-micro text-[10px] md:text-xs flex items-center gap-3 opacity-70"
                            style={{ letterSpacing: "0.4em" }}
                        >
                            <span className="link-ed">{t.cta2.toUpperCase()}</span>
                            <span className="inline-block transition-transform group-hover:translate-x-2">→</span>
                        </Link>
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.4, duration: 1 }}
                className="absolute bottom-8 left-6 md:left-12 right-6 md:right-12 flex items-center justify-between"
            >
                <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", opacity: 0.7 }}>
                    {t.payoff.toUpperCase()}
                </div>
                <motion.span
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="font-micro text-[10px]"
                    style={{ letterSpacing: "0.4em" }}
                >
                    ↓
                </motion.span>
            </motion.div>
        </section>
    );
}

/* ---------- Origins ---------- */
function Origins() {
    const { lang } = useLang();
    const t = SITE.origins[lang];
    return (
        <section id="origins" data-testid="site-origins" className="paper py-32 md:py-48 border-t" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12">
                <div className="col-span-12 md:col-span-5">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-didone font-normal text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            {t.title}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="mt-10 h-px w-24" style={{ background: "#C9A96A" }} />
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-6 md:col-start-7 mt-12 md:mt-4 space-y-8">
                    {t.paragraphs.map((p, i) => (
                        <Reveal key={i} delay={0.15 + i * 0.1}>
                            <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]">{p}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Evolution ---------- */
function Evolution() {
    const { lang } = useLang();
    const t = SITE.evolution[lang];
    return (
        <section id="evolution" data-testid="site-evolution" className="paper-dark py-32 md:py-48" style={{ background: "#0B0B0B", color: "#F4EFE6" }}>
            <div className="column-12">
                <div className="col-span-12 md:col-span-11">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-didone font-normal text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight" style={{ color: "#F4EFE6" }}>
                            {t.title}
                        </h2>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-7 md:col-start-6 mt-12">
                    <Reveal delay={0.2}>
                        <p className="font-serif-lux italic text-xl md:text-2xl leading-[1.5]" style={{ color: "#D9CDB8" }}>
                            {t.body}
                        </p>
                    </Reveal>
                </div>

                <div className="col-span-12 mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-4 gap-8">
                    {t.steps.map((s, i) => (
                        <Reveal key={i} delay={i * 0.12}>
                            <div className="border-t pt-6 h-full" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                                <div className="font-didone italic text-2xl md:text-3xl" style={{ color: "#C9A96A" }}>
                                    {s.year}
                                </div>
                                <h4 className="mt-4 font-didone text-2xl md:text-3xl" style={{ color: "#F4EFE6" }}>
                                    {s.label}
                                </h4>
                                <p className="mt-4 font-serif-lux text-lg opacity-85" style={{ color: "#F4EFE6" }}>
                                    {s.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Mission ---------- */
function Mission() {
    const { lang } = useLang();
    const t = SITE.mission[lang];
    return (
        <section id="mission" data-testid="site-mission" className="paper py-32 md:py-48">
            <div className="column-12">
                <div className="col-span-12 md:col-span-11">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-didone font-normal text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
                            {t.title}
                        </h2>
                    </Reveal>
                </div>

                <div className="col-span-12 md:col-span-7 mt-12">
                    <Reveal delay={0.2}>
                        <p className="font-serif-lux text-xl md:text-2xl lg:text-3xl leading-[1.4]">{t.lead}</p>
                    </Reveal>
                </div>

                <div className="col-span-12 mt-24 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
                    {t.pillars.map((p, i) => (
                        <Reveal key={i} delay={(i % 2) * 0.1}>
                            <div className="border-t pt-6" style={{ borderColor: "rgba(11,11,11,0.35)" }}>
                                <div className="flex items-baseline gap-6">
                                    <span className="font-didone italic text-lg" style={{ color: "#C9A96A" }}>
                                        {p.n}
                                    </span>
                                    <h4 className="font-didone text-3xl md:text-4xl">{p.label}</h4>
                                </div>
                                <p className="mt-5 font-serif-lux text-lg md:text-xl leading-[1.5] opacity-90 max-w-xl">{p.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Editions (what we are now) ---------- */
function Editions() {
    const { lang } = useLang();
    const t = SITE.what[lang];
    const ref = React.useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
    const covers = {
        food: IMAGES.bottles,
        beauty: IMAGES.hands1,
        home: IMAGES.salt2,
    };
    return (
        <section id="editions" data-testid="site-editions" className="paper py-32 md:py-48 border-t" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12">
                <div className="col-span-12 md:col-span-11">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-didone font-normal text-5xl md:text-7xl leading-[0.95] tracking-tight">
                            {t.title}
                        </h2>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-7 md:col-start-6 mt-12">
                    <Reveal delay={0.2}>
                        <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]">{t.body}</p>
                    </Reveal>
                </div>

                {/* Real bottles family shot — hero editorial image */}
                <div className="col-span-12 mt-20" ref={ref}>
                    <Reveal>
                        <motion.div style={{ y }} className="relative overflow-hidden">
                            <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden">
                                <img
                                    src={IMAGES.bottles}
                                    alt="Intollerantia — the family of vessels"
                                    className="w-full h-full object-cover"
                                    data-testid="editions-family-shot"
                                    style={{ filter: "contrast(1.05) saturate(0.85)" }}
                                />
                            </div>
                            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
                                <div className="font-micro text-[10px] mb-2" style={{ letterSpacing: "0.4em", color: "#F4EFE6", textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
                                    {lang === "en" ? "THE FAMILY OF VESSELS" : "LA FAMIGLIA DEI RECIPIENTI"}
                                </div>
                                <div className="font-didone text-2xl md:text-4xl" style={{ color: "#F4EFE6", textShadow: "0 2px 12px rgba(0,0,0,0.5)" }}>
                                    Monocultivar · Blender · Latta · Bottiglia
                                </div>
                            </div>
                            <div className="absolute top-6 right-6 md:top-10 md:right-10 font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#F4EFE6", textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
                                MMXXVII · EDIZIONE I
                            </div>
                        </motion.div>
                    </Reveal>
                </div>

                <div className="col-span-12 mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {t.categories.map((cat, i) => (
                        <Reveal key={cat.id} delay={i * 0.12}>
                            <div className="group" data-testid={`edition-${cat.id}`}>
                                <div className="aspect-[4/5] overflow-hidden relative" style={{ background: "#0B0B0B" }}>
                                    <img
                                        src={covers[cat.id]}
                                        alt={cat.label}
                                        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                        style={{
                                            filter: cat.open
                                                ? "contrast(1.08) saturate(0.5) sepia(0.1)"
                                                : "contrast(1.05) saturate(0.15) sepia(0.4) brightness(0.75)",
                                            opacity: cat.open ? 1 : 0.7,
                                        }}
                                    />
                                    <div className="absolute inset-0 flex items-end p-6">
                                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                            {cat.open ? t.available.toUpperCase() : t.reserved.toUpperCase()}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 flex items-baseline justify-between">
                                    <h4 className="font-didone text-3xl md:text-4xl">{cat.label}</h4>
                                    <span className="font-didone italic text-lg" style={{ color: "#C9A96A" }}>
                                        {`0${i + 1}`}
                                    </span>
                                </div>
                                <p className="mt-3 font-serif-lux text-lg leading-[1.5] opacity-90">{cat.line}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Place ---------- */
function Place() {
    const { lang } = useLang();
    const t = SITE.place[lang];
    const ref = React.useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
    return (
        <section id="place" data-testid="site-place" className="paper py-32 md:py-48 border-t overflow-hidden" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12" ref={ref}>
                <div className="col-span-12 md:col-span-7">
                    <motion.div style={{ y }} className="aspect-[4/5] overflow-hidden">
                        <img src={IMAGES.saline5} alt="Saline di Trapani — Torre di Nubia" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(0.95)" }} />
                    </motion.div>
                </div>
                <div className="col-span-12 md:col-span-4 md:col-start-9 mt-10 md:mt-32">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-didone font-normal text-4xl md:text-6xl leading-[0.95]">{t.title}</h2>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <p className="mt-8 font-serif-lux italic text-xl md:text-2xl leading-[1.4]">{t.body}</p>
                    </Reveal>
                    <Reveal delay={0.35}>
                        <div className="mt-10 font-micro text-[10px] opacity-80" style={{ letterSpacing: "0.32em" }}>
                            <div style={{ color: "#C9A96A" }}>{t.coord}</div>
                            <div className="mt-2">TRAPANI · SICILIA · ITALIA</div>
                            <div className="opacity-70">MARETTIMO · ISOLE EGADI</div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ---------- Salt · Trapani Salt Pans (editorial spread) ---------- */
function Salt() {
    const { lang } = useLang();
    const t = SITE.salt[lang];
    const ref = React.useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const yBig = useTransform(scrollYProgress, [0, 1], [100, -100]);
    const yMid = useTransform(scrollYProgress, [0, 1], [50, -50]);
    const ySmall = useTransform(scrollYProgress, [0, 1], [-30, 30]);

    return (
        <section id="salt" data-testid="site-salt" className="paper py-32 md:py-48 border-t overflow-hidden" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12" ref={ref}>
                {/* Chapter head */}
                <div className="col-span-12 md:col-span-11">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-didone font-normal text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
                            {t.title}
                        </h2>
                    </Reveal>
                </div>

                {/* Lead + massive editorial image */}
                <div className="col-span-12 md:col-span-6 mt-16">
                    <Reveal delay={0.2}>
                        <p className="font-serif-lux text-xl md:text-2xl lg:text-3xl leading-[1.4]">{t.lead}</p>
                    </Reveal>
                </div>

                {/* Full-bleed vertical hero — pink water + salt pyramids */}
                <div className="col-span-12 md:col-span-8 md:col-start-3 mt-20">
                    <Reveal>
                        <motion.div style={{ y: yBig }} className="aspect-[3/4] md:aspect-[4/5] overflow-hidden relative">
                            <img src={IMAGES.saline1} alt="Salt pyramids reflecting on pink brine" className="w-full h-full object-cover" style={{ filter: "contrast(1.03) saturate(1.0)" }} />
                            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#F4EFE6", textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
                                {t.caption.toUpperCase()}
                            </div>
                        </motion.div>
                    </Reveal>
                </div>

                {/* Two-column body */}
                <div className="col-span-12 md:col-span-6 md:col-start-2 mt-24 space-y-8">
                    {t.body.map((p, i) => (
                        <Reveal key={i} delay={i * 0.12}>
                            <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]">{p}</p>
                        </Reveal>
                    ))}
                    <Reveal delay={0.4}>
                        <div className="mt-8 pt-6 border-t grid grid-cols-2 gap-8" style={{ borderColor: "rgba(11,11,11,0.35)" }}>
                            <div>
                                <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                    {t.metaLeft.split(" · ")[0].toUpperCase()}
                                </div>
                                <div className="mt-2 font-didone italic text-xl" style={{ color: "#C9A96A" }}>
                                    {t.metaLeft.split(" · ")[1]}
                                </div>
                            </div>
                            <div>
                                <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                    {t.metaRight.split(" · ")[0].toUpperCase()}
                                </div>
                                <div className="mt-2 font-didone italic text-xl" style={{ color: "#C9A96A" }}>
                                    {t.metaRight.split(" · ")[1]}
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Three-image asymmetric gallery */}
                <div className="col-span-12 md:col-span-3 md:col-start-9 mt-24 md:mt-32">
                    <motion.div style={{ y: yMid }}>
                        <Reveal>
                            <div className="aspect-[3/4] overflow-hidden">
                                <img src={IMAGES.saline4} alt="Terracotta tiles above salt pans" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(1)" }} />
                            </div>
                            <div className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                Fig. I · Egadi · Verso Marettimo
                            </div>
                        </Reveal>
                    </motion.div>
                </div>

                <div className="col-span-12 md:col-span-6 md:col-start-2 mt-16">
                    <Reveal>
                        <div className="aspect-[16/10] overflow-hidden">
                            <img src={IMAGES.saline2} alt="Panoramica delle saline con torre di Nubia" className="w-full h-full object-cover" style={{ filter: "contrast(1.03) saturate(0.95)" }} />
                        </div>
                        <div className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                            Fig. II · Torre di Nubia
                        </div>
                    </Reveal>
                </div>

                <div className="col-span-12 md:col-span-4 md:col-start-9 mt-16">
                    <motion.div style={{ y: ySmall }}>
                        <Reveal delay={0.1}>
                            <div className="aspect-[3/4] overflow-hidden">
                                <img src={IMAGES.saline3} alt="Vasca di evaporazione" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(0.9)" }} />
                            </div>
                            <div className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                Fig. III · Vasca · Evaporazione
                            </div>
                        </Reveal>
                    </motion.div>
                </div>

                {/* Product diptych — the two salt editions */}
                <div className="col-span-12 mt-32 md:mt-48 border-t pt-16 md:pt-20" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
                    <div className="grid grid-cols-12 gap-6">
                        <div className="col-span-12 md:col-span-11">
                            <Reveal>
                                <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                    {t.objectsKicker.toUpperCase()}
                                </div>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h3 className="mt-6 font-didone font-normal text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                                    {t.objectsTitle}
                                </h3>
                            </Reveal>
                        </div>
                        <div className="col-span-12 md:col-span-6 md:col-start-6 mt-8">
                            <Reveal delay={0.2}>
                                <p className="font-serif-lux italic text-xl md:text-2xl leading-[1.45]">{t.objectsBody}</p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
                        {t.products.map((prod, i) => {
                            const img = prod.id === "grani" ? IMAGES.saltJarGrani : IMAGES.saltJarFiocchi;
                            return (
                                <Reveal key={prod.id} delay={i * 0.15}>
                                    <article data-testid={`salt-product-${prod.id}`} className="group">
                                        <div className="relative aspect-[3/4] overflow-hidden" style={{ background: "#D9CDB8" }}>
                                            <img
                                                src={img}
                                                alt={prod.label}
                                                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                                                style={{ filter: "contrast(1.02) saturate(0.95)" }}
                                            />
                                            <div className="absolute top-6 left-6 font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#0B0B0B", opacity: 0.75 }}>
                                                {`0${i + 1} · ${prod.details.toUpperCase()}`}
                                            </div>
                                        </div>

                                        <div className="mt-8 grid grid-cols-12 gap-4">
                                            <div className="col-span-8">
                                                <div className="font-micro text-[10px]" style={{ letterSpacing: "0.32em", color: "#C9A96A" }}>
                                                    {prod.sub.toUpperCase()}
                                                </div>
                                                <h4 className="mt-3 font-didone text-3xl md:text-4xl leading-tight">
                                                    {prod.label}
                                                </h4>
                                            </div>
                                            <div className="col-span-4 text-right">
                                                <span className="font-didone italic text-lg" style={{ color: "#C9A96A" }}>
                                                    {`Ed. 0${i + 1}`}
                                                </span>
                                            </div>
                                        </div>

                                        <p className="mt-5 font-serif-lux text-lg md:text-xl leading-[1.5] opacity-90 max-w-lg">
                                            {prod.body}
                                        </p>
                                        <div className="mt-6 pt-4 border-t flex items-center justify-between font-micro text-[10px]" style={{ borderColor: "rgba(11,11,11,0.2)", letterSpacing: "0.32em" }}>
                                            <span className="opacity-70">{prod.format}</span>
                                            <span style={{ color: "#C9A96A" }}>MMXXVII</span>
                                        </div>
                                    </article>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------- Founder's Note ---------- */
function Founder() {
    const { lang } = useLang();
    const t = SITE.founder[lang];
    return (
        <section id="founder" data-testid="site-founder" className="paper-dark py-32 md:py-48" style={{ background: "#0B0B0B", color: "#F4EFE6" }}>
            <div className="column-12">
                <div className="col-span-12 md:col-span-11">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-didone font-normal text-4xl md:text-6xl leading-[0.95]" style={{ color: "#F4EFE6" }}>
                            {t.title}
                        </h2>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-9 md:col-start-2 mt-12 space-y-8">
                    {t.body.map((line, i) => (
                        <Reveal key={i} delay={i * 0.1}>
                            <p className="font-serif-lux italic text-2xl md:text-3xl lg:text-4xl leading-[1.3]" style={{ color: "#F4EFE6" }}>
                                {line}
                            </p>
                        </Reveal>
                    ))}
                </div>
                <div className="col-span-12 md:col-span-9 md:col-start-2 mt-12 flex items-center gap-6">
                    <div className="h-px w-16" style={{ background: "#C9A96A" }} />
                    <span className="font-didone italic text-2xl" style={{ color: "#C9A96A" }}>
                        {t.sign}
                    </span>
                </div>
            </div>
        </section>
    );
}

/* ---------- Newsletter Circle ---------- */
function Circle() {
    const { lang } = useLang();
    const t = SITE.newsletter[lang];
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle");

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setStatus("sending");
        try {
            await axios.post(`${API}/newsletter`, { email, lang });
            setStatus("ok");
            setEmail("");
        } catch (err) {
            console.error(err);
            setStatus("err");
        }
    };

    return (
        <section id="circle" data-testid="site-circle" className="paper py-32 md:py-48 border-t" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12">
                <div className="col-span-12 md:col-span-6">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-didone font-normal text-4xl md:text-6xl lg:text-7xl leading-[0.95]">{t.title}</h2>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <p className="mt-8 font-serif-lux text-xl md:text-2xl leading-[1.5] opacity-90 max-w-xl">{t.body}</p>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-5 md:col-start-8 mt-12 md:mt-24">
                    <Reveal delay={0.2}>
                        <form onSubmit={submit} data-testid="newsletter-form" className="space-y-6">
                            <label className="font-micro text-[10px] opacity-70 block" style={{ letterSpacing: "0.32em" }}>
                                {t.emailLabel.toUpperCase()}
                            </label>
                            <input
                                data-testid="newsletter-email"
                                required
                                type="email"
                                placeholder={t.placeholder}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-transparent border-b py-3 font-serif-lux text-xl md:text-2xl italic focus:outline-none"
                                style={{ borderColor: "rgba(11,11,11,0.35)" }}
                            />
                            <div className="flex items-center justify-between pt-4">
                                <button
                                    type="submit"
                                    data-testid="newsletter-submit"
                                    disabled={status === "sending"}
                                    className="group font-micro text-xs flex items-center gap-4 disabled:opacity-40"
                                    style={{ letterSpacing: "0.4em" }}
                                >
                                    <span className="link-ed">{(status === "sending" ? t.sending : t.submit).toUpperCase()}</span>
                                    <span className="inline-block transition-transform group-hover:translate-x-2">→</span>
                                </button>
                                <div data-testid="newsletter-status" className="font-micro text-[10px]" style={{ letterSpacing: "0.32em" }}>
                                    {status === "ok" && <span style={{ color: "#3A3E2C" }}>{t.ok.toUpperCase()}</span>}
                                    {status === "err" && <span style={{ color: "#7a1f1f" }}>{t.err.toUpperCase()}</span>}
                                </div>
                            </div>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ---------- Footer ---------- */
function SiteFooter() {
    const { lang } = useLang();
    const t = SITE.footer[lang];
    return (
        <footer className="paper-dark py-16 border-t" style={{ background: "#0B0B0B", color: "#F4EFE6", borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12">
                <div className="col-span-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
                    <div>
                        <Wordmark size="text-3xl md:text-5xl" color="#F4EFE6" tracking="tracking-[0.08em]" />
                        <div className="mt-6 h-px w-24" style={{ background: "#C9A96A" }} />
                        <div className="mt-4 font-micro text-[10px] opacity-80" style={{ letterSpacing: "0.4em" }}>
                            {t.addr.toUpperCase()}
                        </div>
                    </div>

                    <div className="flex items-center">
                        <MonogramSeal size={130} color="#F4EFE6" ring="#C9A96A" />
                    </div>

                    <div className="flex flex-col gap-3 font-micro text-[10px]" style={{ letterSpacing: "0.32em" }}>
                        <button
                            className="link-ed text-left"
                            onClick={() => document.getElementById("circle")?.scrollIntoView({ behavior: "smooth" })}
                            data-testid="footer-contact"
                        >
                            {t.contact.toUpperCase()}
                        </button>
                        <a href="mailto:intollerantia@gmail.com" className="link-ed opacity-70" data-testid="footer-email">
                            intollerantia@gmail.com
                        </a>
                        <a href="https://www.instagram.com/intollerantia" target="_blank" rel="noreferrer" className="link-ed opacity-70">
                            INSTAGRAM
                        </a>
                    </div>
                </div>
                <div className="col-span-12 mt-12 pt-6 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-3" style={{ borderColor: "rgba(201,169,106,0.25)" }}>
                    <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.4em" }}>
                        {t.legal.toUpperCase()}
                    </div>
                    <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.4em" }}>
                        {t.reserved.toUpperCase()}
                    </div>
                </div>
            </div>
        </footer>
    );
}

/* ---------- Announcement Banner ---------- */
function Announcement() {
    const { lang } = useLang();
    const t = SITE.announcement[lang];
    return (
        <section
            id="announcement"
            data-testid="site-announcement"
            className="relative py-24 md:py-32 border-t border-b overflow-hidden"
            style={{ background: "#0B0B0B", color: "#F4EFE6", borderColor: "rgba(201,169,106,0.4)" }}
        >
            {/* Enormous background R */}
            <div
                aria-hidden="true"
                className="absolute -right-12 md:-right-20 top-1/2 -translate-y-1/2 pointer-events-none select-none"
                style={{ opacity: 0.06 }}
            >
                <span
                    className="font-didone leading-none"
                    style={{
                        transform: "scaleX(-1)",
                        display: "inline-block",
                        fontSize: "min(60vw, 60vh)",
                        color: "#C9A96A",
                        fontWeight: 300,
                    }}
                >
                    R
                </span>
            </div>

            <div className="relative column-12">
                <div className="col-span-12 md:col-span-7">
                    <Reveal>
                        <div className="flex items-center gap-4">
                            <motion.span
                                animate={{ opacity: [0.4, 1, 0.4] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ background: "#C9A96A" }}
                            />
                            <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                {t.kicker.toUpperCase()} · MMXXVII
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="mt-6 font-didone font-normal leading-[0.95] tracking-tight"
                            style={{ color: "#F4EFE6", fontSize: "clamp(2.4rem, 7vw, 6rem)" }}
                        >
                            {t.title}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <p className="mt-8 font-serif-lux italic text-xl md:text-2xl leading-[1.45] max-w-2xl" style={{ color: "#D9CDB8" }}>
                            {t.lead}
                        </p>
                    </Reveal>
                </div>

                <div className="col-span-12 md:col-span-4 md:col-start-9 mt-12 md:mt-6 self-end">
                    <Reveal delay={0.3}>
                        <div className="border-t pt-6" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                            <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                {t.stage.toUpperCase()}
                            </div>
                            <div className="mt-4 font-didone text-2xl md:text-3xl" style={{ color: "#F4EFE6" }}>
                                {t.status}
                            </div>
                            <div className="mt-8 space-y-3">
                                <div className="font-micro text-[10px] opacity-80" style={{ letterSpacing: "0.32em" }}>
                                    {t.mediaLabel.toUpperCase()}
                                </div>
                                <p className="font-serif-lux text-lg opacity-80">{t.mediaBody}</p>
                                <a
                                    href={`mailto:${t.actionEmail}?subject=${encodeURIComponent(t.actionLabel + " — Intollerantia")}`}
                                    data-testid="announcement-press-cta"
                                    className="group inline-flex items-center gap-3 font-micro text-[10px] mt-4"
                                    style={{ letterSpacing: "0.4em", color: "#C9A96A" }}
                                >
                                    <span className="link-ed">{t.actionLabel.toUpperCase()}</span>
                                    <span className="inline-block transition-transform group-hover:translate-x-2">→</span>
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ---------- Press Marquee ---------- */
function PressStrip() {
    const { lang } = useLang();
    const t = SITE.press[lang];
    const track = [...t.outlets, ...t.outlets, ...t.outlets];
    return (
        <section id="press" data-testid="site-press" className="paper py-16 md:py-20 border-t border-b" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12 mb-8">
                <div className="col-span-12 flex items-baseline justify-between flex-wrap gap-4">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="font-serif-lux italic text-lg md:text-xl opacity-70">
                            {t.note}
                        </div>
                    </Reveal>
                </div>
            </div>
            <div className="overflow-hidden">
                <div className="marquee-track font-didone" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", letterSpacing: "0.02em" }}>
                    {track.map((o, i) => (
                        <span key={i} className="inline-flex items-center">
                            <span style={{ color: "#0B0B0B", opacity: 0.85 }}>{o}</span>
                            <span className="mx-8 md:mx-14" style={{ color: "#C9A96A", opacity: 0.6 }}>·</span>
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Follow / Sponsor Channels ---------- */
function Follow() {
    const { lang } = useLang();
    const t = SITE.follow[lang];
    return (
        <section id="follow" data-testid="site-follow" className="paper py-32 md:py-48 border-t" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
            <div className="column-12">
                <div className="col-span-12 md:col-span-11">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-didone font-normal text-5xl md:text-7xl leading-[0.95] tracking-tight">
                            {t.title}
                        </h2>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-6 md:col-start-6 mt-12">
                    <Reveal delay={0.2}>
                        <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]">{t.body}</p>
                    </Reveal>
                </div>

                <div className="col-span-12 mt-20 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {t.channels.map((ch, i) => (
                        <Reveal key={ch.id} delay={i * 0.15}>
                            <a
                                href={ch.url}
                                target={ch.id === "email" ? undefined : "_blank"}
                                rel={ch.id === "email" ? undefined : "noreferrer"}
                                data-testid={`follow-${ch.id}`}
                                className="group block border-t pt-8 pb-4 transition-colors"
                                style={{ borderColor: "rgba(11,11,11,0.35)" }}
                            >
                                <div className="flex items-baseline justify-between gap-4">
                                    <div className="flex items-baseline gap-6">
                                        <span className="font-didone italic text-lg" style={{ color: "#C9A96A" }}>
                                            {`0${i + 1}`}
                                        </span>
                                        <h4 className="font-didone text-3xl md:text-4xl">{ch.label}</h4>
                                    </div>
                                    <span className="font-micro text-[10px] group-hover:translate-x-2 transition-transform inline-block" style={{ letterSpacing: "0.32em" }}>
                                        ↗
                                    </span>
                                </div>
                                <div className="mt-6 font-didone italic text-xl md:text-2xl" style={{ color: "#C9A96A" }}>
                                    {ch.handle}
                                </div>
                                <p className="mt-3 font-serif-lux text-lg opacity-85 max-w-lg">{ch.note}</p>
                            </a>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Honey · Marettimo Wild Cardo ---------- */
function Honey() {
    const { lang } = useLang();
    const t = SITE.honey[lang];
    const ref = React.useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const yLandscape = useTransform(scrollYProgress, [0, 1], [80, -80]);
    const yJar = useTransform(scrollYProgress, [0, 1], [50, -50]);

    return (
        <section
            id="honey"
            data-testid="site-honey"
            className="py-32 md:py-48 border-t overflow-hidden relative"
            style={{
                background: "#0B0B0B",
                color: "#F4EFE6",
                borderColor: "rgba(201,169,106,0.4)",
            }}
        >
            <div className="column-12" ref={ref}>
                {/* Chapter head */}
                <div className="col-span-12 md:col-span-11">
                    <Reveal>
                        <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                            {t.kicker.toUpperCase()}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="mt-8 font-didone font-normal leading-[0.9] tracking-tight"
                            style={{ color: "#F4EFE6", fontSize: "clamp(3rem, 8vw, 8rem)" }}
                        >
                            {t.title}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-6 font-serif-lux italic text-xl md:text-2xl" style={{ color: "#C9A96A" }}>
                            {t.place}
                        </div>
                    </Reveal>
                </div>

                {/* Two column body */}
                <div className="col-span-12 md:col-span-6 mt-16 md:mt-24">
                    <Reveal delay={0.25}>
                        <p className="font-serif-lux text-xl md:text-2xl lg:text-3xl leading-[1.45]" style={{ color: "#F4EFE6" }}>
                            {t.lead}
                        </p>
                    </Reveal>
                </div>

                {/* Landscape (Marettimo) */}
                <div className="col-span-12 md:col-span-5 md:col-start-8 mt-16 md:mt-24">
                    <motion.div style={{ y: yLandscape }}>
                        <Reveal delay={0.15}>
                            <div className="aspect-[4/5] overflow-hidden">
                                <img
                                    src={IMAGES.marettimo}
                                    alt="Isola di Marettimo — dove cresce il cardo selvatico"
                                    className="w-full h-full object-cover"
                                    style={{ filter: "contrast(1.1) saturate(0.55) sepia(0.1) brightness(0.9)" }}
                                />
                            </div>
                            <div className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                {t.fig.toUpperCase().replace("FIG. IV · THE VESSEL · ONE KILOGRAM OF STILLNESS", "FIG. I · MARETTIMO · ISOLE EGADI").replace("FIG. IV · IL RECIPIENTE · UN CHILO DI QUIETE", "FIG. I · MARETTIMO · ISOLE EGADI")}
                            </div>
                        </Reveal>
                    </motion.div>
                </div>

                {/* Body paragraphs */}
                <div className="col-span-12 md:col-span-6 mt-16 space-y-6">
                    {t.body.map((p, i) => (
                        <Reveal key={i} delay={i * 0.1}>
                            <p className="font-serif-lux text-xl md:text-2xl leading-[1.5]" style={{ color: "#F4EFE6" }}>
                                {p}
                            </p>
                        </Reveal>
                    ))}
                </div>

                {/* Product hero — the honey jar */}
                <div className="col-span-12 mt-32 md:mt-48">
                    <Reveal>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                            <motion.div style={{ y: yJar }} className="md:col-span-7 md:col-start-1" data-testid="honey-product-hero">
                                <div className="aspect-[4/5] md:aspect-[3/4] overflow-hidden relative" style={{ background: "#D9CDB8" }}>
                                    <img
                                        src={IMAGES.honeyJar}
                                        alt={t.title}
                                        className="w-full h-full object-cover"
                                        style={{ filter: "contrast(1.02) saturate(1)" }}
                                    />
                                    <div className="absolute top-6 left-6 font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#0B0B0B", opacity: 0.8 }}>
                                        {t.objectLabel.toUpperCase()} · MMXXVII
                                    </div>
                                </div>
                            </motion.div>

                            <div className="md:col-span-4 md:col-start-9 flex flex-col justify-end pb-4">
                                <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                    {t.edition.toUpperCase()}
                                </div>
                                <h3 className="mt-4 font-didone text-3xl md:text-4xl leading-tight" style={{ color: "#F4EFE6" }}>
                                    {t.title}
                                </h3>
                                <div className="mt-2 font-serif-lux italic text-lg" style={{ color: "#C9A96A" }}>
                                    {t.place}
                                </div>

                                <div className="mt-10 pt-6 border-t divide-y" style={{ borderColor: "rgba(201,169,106,0.4)" }}>
                                    {t.meta.map((row, i) => (
                                        <div key={i} className="flex items-baseline justify-between py-3" style={{ borderColor: "rgba(201,169,106,0.25)" }}>
                                            <span className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em", color: "#F4EFE6" }}>
                                                {row.k.toUpperCase()}
                                            </span>
                                            <span className="font-serif-lux italic text-lg" style={{ color: "#C9A96A" }}>
                                                {row.v}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ---------- Main Website Page ---------- */
export default function Website() {
    return (
        <SmoothScroll enabled>
            <div className="grain relative">
                <SiteNav />
                <main>
                    <Hero />
                    <Announcement />
                    <Origins />
                    <Marquee text={"INTOLLERANTIA · MAISON ITALIANA · EDIZIONI RARE"} slow />
                    <Evolution />
                    <Mission />
                    <Editions />
                    <Salt />
                    <Honey />
                    <Place />
                    <Founder />
                    <Follow />
                    <Circle />
                    <SiteFooter />
                </main>
            </div>
        </SmoothScroll>
    );
}
